// Core
import React from 'react';

// Libraries
import dayjs from 'dayjs';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

// Types
import { Theme } from '../../types/theme';

// Layout
import { Col } from '../elements/Col';
import {
  LinkTo,
  LinkToColor,
  LinkToSize,
  LinkToType,
  LinkToWeight,
} from '../elements/LinkTo';
import { Logo, LogoSize } from '../elements/Logo';
import {
  Paragraph,
  ParagraphAlign,
  ParagraphSize,
} from '../elements/Paragraph';
import { Row } from '../elements/Row';

// Component
import { LeftPanelImageRightProps } from './LeftPanelImageRight.types';
import {
  ContentContainer,
  Image,
  ImageContainer,
  NeedHelpContainer,
  LeftPanel,
  LogoContainer,
  CoverContainer,
} from './LeftPanelImageRight.style';

function LeftPanelImageRight({ children }: LeftPanelImageRightProps) {
  // Dependencies
  const theme: Theme = useTheme();
  const { t } = useTranslation();

  // Current year
  const currentYear: number = dayjs().year();

  // Help email
  const vantageHelpEmail: string = 'vantagehelp@superior-ind.com';

  return (
    <Row>
      <Col xs={24} sm={24} md={24} lg={9} xl={9} xxl={9}>
        <CoverContainer data-testid="cover-container">
          <LeftPanel data-testid="left-panel">
            <Row>
              <Col xs={24} sm={24} md={24} lg={24} xl={8} xxl={8}>
                <LogoContainer data-testid="logo-container">
                  <Logo size={LogoSize.xs} />
                </LogoContainer>
              </Col>

              <Col xs={0} sm={0} md={0} lg={0} xl={16} xxl={16}>
                <NeedHelpContainer data-testid="need-help-container-xl">
                  <Paragraph align={ParagraphAlign.right}>
                    <LinkTo
                      color={LinkToColor.neutral}
                      type={LinkToType.link}
                      target={`mailto:${vantageHelpEmail}`}
                      size={LinkToSize.xs}
                      weight={LinkToWeight.regular}
                      newTab
                    >
                      {t('layout.leftPanelImageRight.needHelp')}
                    </LinkTo>
                  </Paragraph>
                </NeedHelpContainer>
              </Col>
            </Row>

            <ContentContainer data-testid="content-container">
              {children}
            </ContentContainer>

            <Row>
              <Col xs={24} sm={24} md={24} lg={24} xl={0} xxl={0}>
                <NeedHelpContainer data-testid="need-help-container-xs-sm-md-lg">
                  <Paragraph align={ParagraphAlign.center}>
                    <LinkTo
                      color={LinkToColor.neutral}
                      type={LinkToType.link}
                      target={`mailto:${vantageHelpEmail}`}
                      size={LinkToSize.xs}
                      weight={LinkToWeight.regular}
                      newTab
                    >
                      {t('layout.leftPanelImageRight.needHelp')}
                    </LinkTo>
                  </Paragraph>
                </NeedHelpContainer>
              </Col>
            </Row>

            <Paragraph
              size={ParagraphSize.xs}
              align={ParagraphAlign.center}
              color={theme.color.leftPanelImageRight.textCopyrightColor}
            >
              {t('common.copyright', { year: currentYear })}
            </Paragraph>
          </LeftPanel>
        </CoverContainer>
      </Col>

      <Col xs={0} sm={0} md={0} lg={15} xl={15} xxl={15}>
        <ImageContainer data-testid="image-container">
          <Image data-testid="login-background-image" />
        </ImageContainer>
      </Col>

    </Row>
  );
}

export { LeftPanelImageRight };
